import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import {
  Attachment,
  AttachmentData,
  AttachmentType,
  DownloadTableCell,
  DownloadTableCellType,
  DownloadTableData,
  DownloadTableRow,
  GenericProgressStatus,
  LearnizInteractivity,
  LearnizInteractivityType,
  LearnizMedia,
  LearnizMediaType,
  LearnizModel,
  LearnizProgression,
  LearnizSlide,
  SSOLinkMode,
} from '@jump-tools-front/shared-interfaces';
import { Store } from '@ngxs/store';
import { first, map } from 'rxjs/operators';
import { CONTENT_SLUG, LearnizService, LearnizState, WordingPipe } from '@jump-tools-front/shared';
import { StatsComponent } from '../../../../shared/components/ui/stats/stats.component';
import { AsyncPipe, DecimalPipe, NgClass } from '@angular/common';

@Component({
  selector: 'learniz-landing-stats',
  templateUrl: './landing-stats.component.html',
  styleUrls: ['../landing-section.scss', './landing-stats.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgClass, StatsComponent, AsyncPipe, DecimalPipe, WordingPipe],
})
export class LandingStatsComponent {
  private _store = inject(Store);
  private _learnizService = inject(LearnizService);
  readonly linkMode = SSOLinkMode;
  readonly learniz = this._store.selectSnapshot<LearnizModel>(LearnizState.getLearniz);
  readonly mode = this._store.selectSnapshot<SSOLinkMode>(LearnizState.mode);
  readonly stats$ = this._learnizService
    .getStats$(localStorage.getItem(CONTENT_SLUG)) //
    .pipe(
      first(),
      map(stats => {
        const allInteractivities = this.learniz.chapters
          .reduce<LearnizSlide[]>((arr, c) => arr.concat(c.slides), [])
          .reduce<LearnizInteractivity[]>((arr, s) => arr.concat(s.interactivities), []);
        const medias = allInteractivities
          .filter(i => i.type === LearnizInteractivityType.MEDIA_PLAYER)
          .reduce<LearnizMedia<any>[]>((arr, i) => arr.concat(i.medias), []);

        const hasVideo = medias.some(m => m.type === LearnizMediaType.VIDEO);
        const hasPodcast = medias.some(m => m.type === LearnizMediaType.AUDIO);
        const hasGame = allInteractivities.some(i => i.type === LearnizInteractivityType.GAME);
        const hasAttachmentDownload = allInteractivities
          .filter(i => i.type === LearnizInteractivityType.ATTACHMENT)
          .reduce<AttachmentData[]>((arr, i) => arr.concat(<AttachmentData>i.data), [])
          .reduce<Attachment[]>((arr, a) => arr.concat(a.attachments), [])
          .some(a => a.type === AttachmentType.FILE);
        const hasTableDownload = allInteractivities
          .filter(i => i.type === LearnizInteractivityType.DOWNLOAD_TABLE)
          .reduce<DownloadTableData[]>((arr, i) => arr.concat(<DownloadTableData>i.data), [])
          .reduce<DownloadTableRow[]>((arr, dt) => arr.concat(dt.table.rows), [])
          .reduce<DownloadTableCell[]>((arr, r) => arr.concat(r.cells), [])
          .some(c => c.contentType === DownloadTableCellType.FILE && c.contents.length);

        const progress = this._store.selectSnapshot<LearnizProgression>(LearnizState.getProgress);
        return {
          percentage: progress.percentage,
          nbChapters: progress.chapters.length,
          nbChaptersComplete: progress.chapters.filter(c => c.status === GenericProgressStatus.COMPLETE).length,
          duration: progress.time,
          nbVideo: stats?.stats?.nbVideo || 0,
          nbPodcast: stats?.stats?.nbPodcast || 0,
          nbGames: stats?.stats?.nbGames || 0,
          nbDownload: stats?.stats?.nbDownload || 0,
          hasVideo,
          hasPodcast,
          hasGame,
          hasDownload: hasAttachmentDownload || hasTableDownload,
        };
      }),
    );
}
